import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import styles from './WinRateGraph.module.scss';
import { CompanyPage, CompanyPageBid } from '../../../../../../hooks/slices/companySlice';
import { DateUtil } from '../../../../../../utils/date';
import { PieGraph } from '../../../../../../components/graphs/pieGraph';
import { BarGraphStacked } from '../../../../../../components/graphs';
import {
    calculateMetricByYear,
    calculateYoYChange,
    getSimpleYoYSubtitleProps,
    reduceBidsByYear,
    SimpleYoYSubtitleResult
} from '../../../info/infoUtils';


interface WinRateGraphProps {
    companyPage: CompanyPage;
}

export const WinRateGraph: React.FC<WinRateGraphProps> = (props) => {
    const { t } = useTranslation();
    const companyPage: CompanyPage = props.companyPage;
    const bids: CompanyPageBid[] = companyPage.bids ?? [];

    const [showSummaryView, setShowSummaryView] = useState(true);


    const allWinsSummary = bids.filter(bid => bid.bidRole === "WINNER").length;
    const allLossesSummary = bids.filter(bid => bid.bidRole === "LOSER").length;
    const allNotKnownSummary = bids.length - allWinsSummary - allLossesSummary;


    const currentYear = DateUtil.today().year();
    const lastYear = currentYear - 1;
    const relevantYears = [lastYear, currentYear];


    const yearlyBids = reduceBidsByYear(bids);


    type WinLossMetric = { wins: number; losses: number };
    const yearlyWinLossMetrics = calculateMetricByYear<WinLossMetric>(
        yearlyBids,
        relevantYears,
        (bidsInYear: CompanyPageBid[]) => {
            const wins = bidsInYear.filter(bid => bid.bidRole === "WINNER").length;
            const losses = bidsInYear.filter(bid => bid.bidRole === "LOSER").length;
            return { wins, losses };
        },
        { wins: 0, losses: 0 }
    );

    const fiveYearRange = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);
    const bidsLast5Years = bids.filter(bid => {
        const dateToUse = bid.awardDate ?? bid.deadline;
        if (!dateToUse) return false;
        const bidYear = new Date(dateToUse).getFullYear();
        return fiveYearRange.includes(bidYear);
    });
    const winsLast5Years = bidsLast5Years.filter(bid => bid.bidRole === "WINNER").length;
    const lossesLast5Years = bidsLast5Years.filter(bid => bid.bidRole === "LOSER").length;
    const totalRelevantBids5Y = winsLast5Years + lossesLast5Years;
    const notKnownLast5Years = bidsLast5Years.length - winsLast5Years - lossesLast5Years;
    const fiveYearWinRate = totalRelevantBids5Y > 0 ? (winsLast5Years / totalRelevantBids5Y) * 100 : null;



    const calculateRate = (metric: WinLossMetric): number => {
        const total = metric.wins + metric.losses;
        return total > 0 ? (metric.wins / total) * 100 : 0;
    };

    const currentYearWinRate = calculateRate(yearlyWinLossMetrics[currentYear.toString()]);
    const lastYearWinRate = calculateRate(yearlyWinLossMetrics[lastYear.toString()]);


    const yearOverYearChange = calculateYoYChange(currentYearWinRate, lastYearWinRate, 'percentagePoints');

    const hasSufficientData = yearOverYearChange !== null;

    const subtitleProps : SimpleYoYSubtitleResult | null = hasSufficientData ? getSimpleYoYSubtitleProps({
        yearOverYearChange: yearOverYearChange!,
        t: t,
        increaseKey: "companyPage.win_ratio_increase",
        decreaseKey: "companyPage.win_ratio_decrease",
        noChangeKey: "companyPage.win_ratio_no_change",
    }) : null;

    const changeText = hasSufficientData ? Math.abs(yearOverYearChange!).toFixed(1) : '';

    const toggleView = () => {
        setShowSummaryView(!showSummaryView);
    };

    return (
        <div
            className={`
                ${styles["graph-card"]}
                ${styles["winrate-card"]}
                ${showSummaryView ? styles["show-summary"] : styles["show-details"]}
            `}
            onClick={toggleView}
            style={{ cursor: 'pointer' }}
            title={showSummaryView
                    ? t('companyPage.winrateGraph.tooltipExpand')
                    : t('companyPage.winrateGraph.tooltipCollapse')}
        >
            <div className={styles["heading-container"]}>
                <span className={styles["graph-type-title"]}>{t("companyPage.win_ratio_graph_type", "Winrate")}</span>

                <h3 className={styles["last-year-percentage"]}>{`${fiveYearWinRate?.toFixed(0) ?? companyPage.percentageOfBidsWon?.toFixed(0) ?? 'N/A'}%`}</h3>

                 {!hasSufficientData && (
                    <div className={styles.subtitle}>
                        {t("companyPage.win_ratio_title_2", "vs last year")}
                    </div>
                 )}
            </div>
            <div className={styles["graph-container"]}>
                {showSummaryView ? (
                    <WinRateSummaryGraph
                        wins={winsLast5Years}
                        losses={lossesLast5Years}
                        notKnown={notKnownLast5Years}
                    />
                ) : (

                    <WinRateDetailsGraph bids={bids} />
                )}
            </div>
        </div>
    );
}



interface WinRateSummaryGraphProps {
    wins: number;
    losses: number;
    notKnown: number;
}

const WinRateSummaryGraph: React.FC<WinRateSummaryGraphProps> = ({ wins, losses, notKnown }) => {
    const { t } = useTranslation();
    const total = wins + losses + notKnown;
    const hasData = total > 0;

    const graphData = [
        { x: 'won', y: wins, label: t('companyPage.bidStatus.won', 'Won') },
        { x: 'lost', y: losses, label: t('companyPage.bidStatus.lost', 'Lost') },
        { x: 'not_known', y: notKnown, label: t('companyPage.bidStatus.not_known', 'Not Known') },
    ].filter(d => d.y > 0);

    return (

        <div style={{ height: '150px', width: '100%' }}>
            {hasData ? (
                 <PieGraph data={graphData} />
            ) : (

                 <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    {t('companyPage.no_bid_data_current_year', 'No bid data found for the current year.')}
                 </div>
            )}
        </div>
    );
}

interface WinRateDetailsGraphProps {
    bids: CompanyPageBid[];
}
const WinRateDetailsGraph: React.FC<WinRateDetailsGraphProps> = (props) => {
    const bids: CompanyPageBid[] = props.bids;
    const { t } = useTranslation();

    const currentYr = DateUtil.today().year();
    const years = Array.from({length: 5}, (_, i) => currentYr - 4 + i);


    const bidCountsByYear = years.map(year => {
        const yearBids = bids.filter(bid => {

            const dateToUse = bid.awardDate ?? bid.deadline;

            return dateToUse ? (new Date(dateToUse).getFullYear() === year) : false;
        });
        const yearWins = yearBids.filter(bid => bid.bidRole === "WINNER").length;
        const yearLosses = yearBids.filter(bid => bid.bidRole === "LOSER").length;
        const yearNotKnown = yearBids.length - yearWins - yearLosses;

        return {
            x: year.toString(),
            y: [yearWins, yearLosses, yearNotKnown],
            label: [
                t('companyPage.bidStatus.won'),
                t('companyPage.bidStatus.lost'),
                t('companyPage.bidStatus.not_known'),
            ]
        };
    });

    const maxTotalBids = Math.max(0, ...bidCountsByYear.map(d => d.y.reduce((a, b) => a + b, 0)));

    return (
            <BarGraphStacked
                isStacked={true}
                data={bidCountsByYear}
                showYAxis={true}
                yDomain={[0, Math.max(5, Math.ceil(maxTotalBids * 1.1))]}

            />
    );
} 